import { getSessionTransferUrl } from '@lib/utils/session-transfer'

import { getConfig } from '@config/index'

import { ComponentCtaButtonType } from '@constants/enums/cta'

import { firstEligibleInstall, GoInstallType } from './go/go-install-eligibility'
import { useExtensionAvailability } from './go/use-extension-availibility'
import {
  useIsEligibleForDownload,
  useIsEligibleForExtension,
} from './mail/mail-install-eligibility'

const config = getConfig()

export interface ProductCtaData {
  text: string
  url?: string
  underLinkText?: string
  dataAttributes?: string[]
}

type ProductType = Extract<
  ComponentCtaButtonType,
  | ComponentCtaButtonType.Coda
  | ComponentCtaButtonType.Grammarly
  | ComponentCtaButtonType.Go
  | ComponentCtaButtonType.Mail
>

const goTextMap: Record<string, string> = {
  installed: 'Open',
  enableGo: 'Turn on Go with Grammarly',
  chromeInstall: 'Install Grammarly for Chrome',
  edgeInstall: 'Install Grammarly for Edge',
  chromeFallback: 'Learn about Grammarly for Chrome',
  chromeFallbackUnderLink: 'Available on Chrome today, with more browsers coming soon.',
}

// TODO - localize text
const mailTextMap: Record<string, string> = {
  chromeInstall: 'Install Mail for Chrome',
  download: 'Download Mail',
  chromeFallback: 'Learn about Mail for Chrome',
}

const getCtaFollowUrlWithSessionTransfer = (
  product: Exclude<ProductType, ComponentCtaButtonType.Go | ComponentCtaButtonType.Mail>,
): ProductCtaData => {
  switch (product) {
    case ComponentCtaButtonType.Coda: {
      return {
        text: 'Start using Coda',
        url: getSessionTransferUrl(config.sessionTransfer.toCoda, {
          rpHost: config.domains.coda,
          rpPath: config.paths.coda.onboarding,
        }),
      }
    }
    case ComponentCtaButtonType.Grammarly: {
      return {
        text: 'Get Grammarly',
        url: getSessionTransferUrl(config.sessionTransfer.toGrammarly, {
          rpHost: config.domains.grammarly,
          rpPath: config.paths.grammarly.personalize,
        }),
      }
    }
  }
}

const getGoCtaFollowUrlWithSessionTransfer = (
  goInstallType: GoInstallType | undefined,
  isExtensionWithoutGo: boolean,
  isGoAvailable: boolean,
): ProductCtaData => {
  if (isGoAvailable) {
    return {
      text: goTextMap.installed,
      // data-superhuman-go-open attribute is used by Extension to open Go side panel
      // see https://coda.io/d/Air-Traffic-Control_dG9w8N7nitW/ATC-Go-Extension_sujhfANB#_luIQMpKq
      dataAttributes: ['data-superhuman-go-open'],
    }
  }
  if (goInstallType && isExtensionWithoutGo) {
    return {
      text: goTextMap.enableGo,
    }
  }

  switch (goInstallType) {
    case GoInstallType.Chrome: {
      return {
        text: goTextMap.chromeInstall,
        url: getSessionTransferUrl(config.sessionTransfer.toGrammarly, {
          rpHost: config.products.go.extension.chrome.downloadDomain,
          rpPath: config.products.go.extension.chrome.downloadPath,
        }),
      }
    }
    case GoInstallType.Edge: {
      return {
        text: goTextMap.edgeInstall,
        url: getSessionTransferUrl(config.sessionTransfer.toGrammarly, {
          rpHost: config.products.go.extension.edge.downloadDomain,
          rpPath: config.products.go.extension.edge.downloadPath,
        }),
      }
    }
    default: {
      return {
        text: goTextMap.chromeFallback,
        underLinkText: goTextMap.chromeFallbackUnderLink,
        url: config.products.go.extension.chrome.downloadUrl,
      }
    }
  }
}

const getMailCtaUrl = (isEligibleForMailExtension: boolean, isEligibleForMailDownload: boolean) => {
  if (isEligibleForMailExtension) {
    return {
      text: mailTextMap.chromeInstall,
      url: config.products.mail.extension.chrome.downloadUrl,
    }
  }

  if (isEligibleForMailDownload) {
    return {
      text: mailTextMap.download,
      url: config.products.mail.appDownloadUrl,
    }
  }

  return {
    text: mailTextMap.chromeFallback,
    url: config.products.mail.extension.chrome.downloadUrl,
  }
}

export const useProductCta = (product: ProductType): ProductCtaData => {
  const goInstallType = firstEligibleInstall()
  const { isExtensionWithoutGo, isGoAvailable } = useExtensionAvailability()
  const isEligibleForMailExtension = useIsEligibleForExtension()
  const isEligibleForMailDownload = useIsEligibleForDownload()

  switch (product) {
    case ComponentCtaButtonType.Coda: {
      return getCtaFollowUrlWithSessionTransfer(product)
    }
    case ComponentCtaButtonType.Grammarly: {
      return getCtaFollowUrlWithSessionTransfer(product)
    }
    case ComponentCtaButtonType.Go: {
      return getGoCtaFollowUrlWithSessionTransfer(
        goInstallType,
        isExtensionWithoutGo,
        isGoAvailable,
      )
    }
    case ComponentCtaButtonType.Mail: {
      return getMailCtaUrl(isEligibleForMailExtension, isEligibleForMailDownload)
    }
  }
}
